import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Send, Copy, ExternalLink, Trash2, RefreshCcw, Smartphone, Eye,
} from "lucide-react";

const CUT_OPTIONS = [
  { value: "marketing_2min", label: "2-min public marketing reel" },
  { value: "flagship_3min", label: "3-min portal flagship" },
  { value: "full_7min", label: "7-min full walkthrough" },
  { value: "vendor_partnership", label: "Vendor partnership film" },
];

/**
 * Admin · SMS-share demo videos with one click.
 *
 * - Admin enters recipient name + phone, picks a cut, hits Send.
 * - Backend mints a tokenized /watch/{token} URL and SMSes it via Twilio.
 * - Past links visible in the audit table — click count, last clicked,
 *   resend SMS, revoke, copy link.
 */
export default function VideoShareSmsPanel() {
  const [cut, setCut] = useState("marketing_2min");
  const [recipientName, setRecipientName] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);

  const loadLinks = async () => {
    setLoadingLinks(true);
    try {
      const r = await api.get("/admin/video-share/links");
      setLinks(r.data || []);
    } catch (e) {
      // Silent — table will be empty.
    } finally {
      setLoadingLinks(false);
    }
  };

  useEffect(() => {
    loadLinks();
  }, []);

  const send = async () => {
    if (!recipientPhone.trim()) {
      toast.error("Recipient phone is required.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/video-share/links", {
        cut,
        recipient_name: recipientName.trim() || null,
        recipient_phone: recipientPhone.trim(),
        send_sms_now: true,
        expires_in_days: 30,
        custom_message: customMessage.trim() || null,
      });
      const d = r.data || {};
      if (d.sms_sent) {
        toast.success(`SMS sent to ${recipientPhone}.`);
      } else {
        toast.warning(`Link created, but SMS failed: ${d.sms_error || "unknown"}`);
      }
      setRecipientName("");
      setRecipientPhone("");
      setCustomMessage("");
      loadLinks();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const resend = async (token) => {
    try {
      const r = await api.post(`/admin/video-share/links/${token}/resend`);
      if (r.data?.sms_sent) toast.success("SMS resent.");
      else toast.error(`Resend failed: ${r.data?.sms_error || "unknown"}`);
      loadLinks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? Anyone holding it will see an 'unavailable' page.")) return;
    try {
      await api.delete(`/admin/video-share/links/${token}`);
      toast.success("Link revoked.");
      loadLinks();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyUrl = (token) => {
    const origin = window.location.origin;
    navigator.clipboard.writeText(`${origin}/watch/${token}`);
    toast.success("Link copied to clipboard.");
  };

  return (
    <section
      data-testid="video-share-sms-panel"
      className="mt-10 rounded-2xl border border-slate-200 bg-white p-6"
    >
      <div className="flex items-start gap-4 flex-wrap mb-5">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#1F4853] flex items-center gap-2">
            <Smartphone className="w-3.5 h-3.5" /> Video share · SMS
          </div>
          <h3 className="mt-1 font-display text-2xl font-bold text-slate-900 tracking-tight">
            Send any demo video by text — straight from this dashboard.
          </h3>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Mints a tokenized <span className="font-mono text-[12px]">/watch/{"{token}"}</span> URL on a branded
            no-login landing page, then fires an SMS via Twilio.
            Tracks click count + last-clicked timestamp per link, expires after 30 days.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <label className="block">
          <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
            Cut
          </span>
          <select
            value={cut}
            onChange={(e) => setCut(e.target.value)}
            data-testid="video-share-cut-select"
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1F4853]/30"
          >
            {CUT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
            Recipient phone (E.164)
          </span>
          <input
            value={recipientPhone}
            onChange={(e) => setRecipientPhone(e.target.value)}
            placeholder="+15555550123"
            data-testid="video-share-phone-input"
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4853]/30"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
            Recipient name (optional)
          </span>
          <input
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Dr. Jane Smith"
            data-testid="video-share-name-input"
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4853]/30"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
            Custom intro line (optional)
          </span>
          <input
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder="Great connecting at HLTH — here's that video I mentioned."
            data-testid="video-share-message-input"
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4853]/30"
          />
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={send}
          disabled={sending || !recipientPhone.trim()}
          data-testid="video-share-send-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#1F4853] text-white px-5 py-2.5 text-sm font-bold hover:bg-[#163740] disabled:opacity-50"
        >
          <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send via SMS"}
        </button>
        <button
          type="button"
          onClick={loadLinks}
          data-testid="video-share-refresh-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loadingLinks ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="mt-6 border-t border-slate-200 pt-5">
        <h4 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
          Sent links · last 200
        </h4>
        {links.length === 0 ? (
          <div className="text-sm text-slate-500 italic">No share links yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="video-share-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-bold py-2 pr-3">Recipient</th>
                  <th className="text-left font-bold py-2 pr-3">Cut</th>
                  <th className="text-left font-bold py-2 pr-3">SMS</th>
                  <th className="text-left font-bold py-2 pr-3">Clicks</th>
                  <th className="text-left font-bold py-2 pr-3">Created</th>
                  <th className="text-right font-bold py-2"></th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.token} className="border-b border-slate-100 align-top" data-testid={`video-share-row-${l.token}`}>
                    <td className="py-3 pr-3">
                      <div className="font-semibold text-slate-900">{l.recipient_name || "—"}</div>
                      <div className="text-xs text-slate-500 font-mono">{l.recipient_phone || ""}</div>
                    </td>
                    <td className="py-3 pr-3 text-slate-700">{l.cut_label}</td>
                    <td className="py-3 pr-3">
                      <SmsStatusPill status={l.sms_status} error={l.sms_error} />
                    </td>
                    <td className="py-3 pr-3">
                      <div className="font-bold text-slate-900">{l.click_count || 0}</div>
                      {l.last_clicked_at && (
                        <div className="text-[11px] text-slate-500">
                          {new Date(l.last_clicked_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                        </div>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-xs text-slate-500">
                      {l.created_at ? new Date(l.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : ""}
                    </td>
                    <td className="py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <a
                          href={l.public_url_path}
                          target="_blank"
                          rel="noreferrer"
                          title="Open watch page"
                          data-testid={`video-share-open-${l.token}`}
                          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </a>
                        <button
                          type="button"
                          onClick={() => copyUrl(l.token)}
                          title="Copy share URL"
                          data-testid={`video-share-copy-${l.token}`}
                          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        {l.recipient_phone && (
                          <button
                            type="button"
                            onClick={() => resend(l.token)}
                            title="Resend SMS"
                            data-testid={`video-share-resend-${l.token}`}
                            className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
                          >
                            <Send className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => revoke(l.token)}
                          title="Revoke link"
                          data-testid={`video-share-revoke-${l.token}`}
                          className="p-1.5 rounded-md hover:bg-rose-50 text-rose-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function SmsStatusPill({ status, error }) {
  const map = {
    sent: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500", label: "Sent" },
    queued: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500", label: "Queued" },
    failed: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500", label: "Failed" },
    not_sent: { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", dot: "bg-slate-400", label: "Not sent" },
  };
  const s = map[status] || map.not_sent;
  return (
    <span
      title={error || status}
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold border rounded-full px-2.5 py-1 ${s.bg} ${s.text} ${s.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}
